/**
 * Shared calendar-date primitives.
 *
 * No date library is installed (only FullCalendar's own packages), so these are
 * the canonical hand-rolled helpers — kept in ONE place instead of being
 * re-implemented per feature. All operate on **local** calendar dates (no
 * timezone shift), which is what `<input type="date">` values and date-only API
 * query params (`YYYY-MM-DD`) require.
 */

/** Local calendar date as `YYYY-MM-DD` (no timezone shift). */
export function toLocalDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Parse a `YYYY-MM-DD` value into a local `Date` at the given hour (default
 * midnight). Pass `hour = 12` for day-arithmetic that must be DST-safe (noon
 * never crosses a day boundary when the clock shifts).
 */
export function parseLocalDate(value: string, hour = 0): Date {
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, m - 1, d, hour, 0, 0, 0);
}

/** True when `value` is a well-formed, real `YYYY-MM-DD` calendar date. */
export function isValidDateInputValue(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [y, m, d] = value.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return (
    date.getFullYear() === y &&
    date.getMonth() === m - 1 &&
    date.getDate() === d
  );
}

/** A new `Date` shifted by `n` whole days (does not mutate the input). */
export function addDays(date: Date, n: number): Date {
  const x = new Date(date.getTime());
  x.setDate(x.getDate() + n);
  return x;
}

/** Monday of the week containing `date`, as a new local-midnight `Date`. */
export function startOfWeekMonday(date: Date): Date {
  const c = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = c.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  c.setDate(c.getDate() + offset);
  return c;
}

/** Shift a `YYYY-MM-DD` value by `n` whole days, returning `YYYY-MM-DD`.
 *  Parses at local noon so the result is DST-safe. */
export function shiftDateInputValue(value: string, n: number): string {
  return toLocalDateString(addDays(parseLocalDate(value, 12), n));
}
